/**
 * «Crear recado»: the grantor scans the bearer's identity, writes what the
 * bearer may do on their behalf, and shows the signed errand as a QR for the
 * bearer's wallet to keep. No network at any step.
 */
import { ATTR } from "../constants";
import {
  createErrand, ERRAND_TEXT_FLOOR, ERRAND_TTL_CHOICES, errandExpiry, errandTextBudget, textBytes,
} from "../errand";
import { decodePresentation } from "../format";
import { emitQR } from "../qr";
import { receiptDraft, recordReceipt } from "../receipts";
import { startScanner } from "../scan";
import { checkStatus } from "../status";
import {
  correctedNow, loadCredentialLite, loadDeviceKey, loadStatusList, loadTrustStore,
} from "../storage";
import { expiresAtOf } from "../sync";
import { escapeHTML, formatWhen, REASON_COPY, type RefusalReason } from "../ui";
import { verifyPresentation } from "../verify";
import { scanOnce } from "./camera";
import type { Scanner } from "./scan";
import { ICON } from "../icons";

interface Bearer {
  uid: string;
  name: string;
  role: string;
  credentialBytes: Uint8Array;
}

export async function renderErrandCreate(
  root: HTMLElement,
  onDone: () => void,
  scanner: Scanner = startScanner,
): Promise<() => void> {
  let stopCamera: () => void = () => {};
  const lite = await loadCredentialLite();
  const pair = await loadDeviceKey();

  /** A sentence and a way out, for every refusal on this screen. */
  const stopWith = (title: string, sentence: string, again: boolean) => {
    root.innerHTML = `<div class="problem"><div class="badge">${ICON.cross}</div>
      <h1>${escapeHTML(title)}</h1><p>${escapeHTML(sentence)}</p>
      <div class="foot">${again ? `<button id="again">Escanear otra vez</button>` : ""}
      <button id="cancel" class="ghost">Volver a mi identidad</button></div></div>`;
    root.querySelector("#again")?.addEventListener("click", () => void scan());
    root.querySelector("#cancel")!.addEventListener("click", onDone);
  };

  const scan = async () => {
    stopCamera = await scanOnce(
      root, "Escanea el código de identidad de la persona que hará el recado",
      (payload) => void onScanned(payload), scanner,
    );
  };

  /** A normal verification of the bearer, against what this phone holds. */
  const onScanned = async (payload: Uint8Array) => {
    let reason: RefusalReason;
    let bearer: Bearer | null = null;
    try {
      const { now, synced } = await correctedNow();
      const statusList = await loadStatusList();
      const result = await verifyPresentation(payload, {
        issuerPublicKeys: await loadTrustStore(),
        now,
        clockSynced: synced,
        // Not remembered: the errand names this person and only their device
        // key can present it, so a replayed identity here buys nothing. No
        // pending challenges either: this screen issues none, so a strong-mode
        // code is refused with `challenge-unknown`.
        seenNonces: new Set(),
        status: (index) => checkStatus(statusList, index),
      });
      reason = result.reason;
      if (result.ok && result.attributes) {
        bearer = {
          uid: String(result.attributes.get(ATTR.ID)),
          name: String(result.attributes.get(ATTR.NAME) ?? ""),
          role: String(result.attributes.get(ATTR.ROLE) ?? ""),
          credentialBytes: decodePresentation(payload).credentialBytes,
        };
      }
    } catch {
      reason = "device-error";
    }
    // This scan is a verification like any other, so it goes into both
    // people's history the way `renderScan` puts it there: accepted or
    // refused, with the reason this screen showed, and never in the way of
    // the screen.
    const draft = receiptDraft(payload, reason);
    if (draft) void recordReceipt(draft).catch(() => {});
    if (!bearer) {
      stopWith("No se puede crear el recado", REASON_COPY[reason], true);
      return;
    }
    await showForm(bearer);
  };

  const showForm = async (bearer: Bearer) => {
    const grantorExpiresAt = expiresAtOf(lite!);
    const { now } = await correctedNow();
    if (grantorExpiresAt <= now) {
      stopWith("No se puede crear el recado",
        "Tu identidad caducó. Conéctate a la red para renovarla y vuelve a intentarlo.", false);
      return;
    }
    const budget = errandTextBudget({
      bearerCredentialBytes: bearer.credentialBytes,
      liteCredentialBytes: lite!,
      bearer: bearer.uid,
    });
    if (budget < ERRAND_TEXT_FLOOR) {
      stopWith("No se puede crear el recado",
        `Tu identidad y la de ${bearer.name} ya llenan el código: solo quedarían ${budget} ` +
        `bytes para el texto, y hacen falta al menos ${ERRAND_TEXT_FLOOR}. Con más texto el ` +
        `código saldría demasiado denso para leerlo con la cámara.`, false);
      return;
    }

    root.innerHTML = `
      <h1>Recado para ${escapeHTML(bearer.name)}</h1>
      <p class="role">${escapeHTML(bearer.role)}</p>
      <form id="errand-form">
        <label>Qué puede hacer en tu nombre
          <textarea name="text" rows="3" required></textarea></label>
        <p id="counter" aria-live="polite"></p>
        <label>Vence en <select name="ttl">
          ${ERRAND_TTL_CHOICES.map((choice) =>
            `<option value="${choice.seconds}">${choice.label}</option>`).join("")}
        </select></label>
        <p id="expires"></p>
        <button type="submit">Firmar recado</button>
      </form>
      <button id="cancel" class="ghost">Cancelar</button>`;
    const form = root.querySelector<HTMLFormElement>("#errand-form")!;
    const text = form.querySelector<HTMLTextAreaElement>("textarea")!;
    const ttl = form.querySelector<HTMLSelectElement>("select")!;
    const counter = root.querySelector<HTMLParagraphElement>("#counter")!;
    const expires = root.querySelector<HTMLParagraphElement>("#expires")!;
    const submit = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    root.querySelector("#cancel")!.addEventListener("click", onDone);

    // Bytes, not characters, because the QR carries bytes: a limit counted in
    // characters would let an accented text through the form and over the
    // version-26 edge.
    const refresh = async () => {
      const used = textBytes(text.value.trim());
      counter.textContent = `${used} de ${budget} bytes. Las tildes y la ñ cuentan doble.`;
      counter.classList.toggle("warn", used > budget);
      submit.disabled = used === 0 || used > budget;
      const { now } = await correctedNow();
      expires.textContent =
        `Vence ${formatWhen(errandExpiry(now, Number(ttl.value), grantorExpiresAt))}`;
    };
    text.addEventListener("input", () => void refresh());
    ttl.addEventListener("change", () => void refresh());
    await refresh();

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void sign(bearer, text.value.trim(), Number(ttl.value), grantorExpiresAt, budget);
    });
  };

  const sign = async (
    bearer: Bearer, text: string, ttl: number, grantorExpiresAt: number, budget: number,
  ) => {
    if (textBytes(text) > budget) return;
    const { now } = await correctedNow();
    const expiresAt = errandExpiry(now, ttl, grantorExpiresAt);
    const errand = await createErrand({
      liteCredentialBytes: lite!, text, bearer: bearer.uid, expiresAt, deviceKey: pair!.privateKey,
    });
    root.innerHTML = `<div class="problem qr-page">
      <h1>Recado firmado</h1>
      <p>Pide a ${escapeHTML(bearer.name)} que abra «Recibir recado» en su teléfono y
      escanee este código.</p>
      <div class="qr" id="errand-qr">${(await emitQR(errand))[0]}</div>
      <div class="errand-block"><p>${escapeHTML(text)}</p>
      <p>Vence ${escapeHTML(formatWhen(expiresAt))}</p></div>
      <div class="foot"><button id="done">Listo</button></div></div>`;
    root.querySelector("#done")!.addEventListener("click", onDone);
  };

  if (!lite || !pair) {
    stopWith("No se puede crear el recado",
      "Tu identidad todavía no permite crear recados. Se actualiza sola la próxima vez que " +
      "el teléfono se conecte a la UH y la renueve.", false);
  } else {
    await scan();
  }
  return () => stopCamera();
}
